import { groupBy, isNil, orderBy, sumBy, keys, values } from 'lodash'
import moment from 'moment'
import { hasValue } from '../../utils/NumberUtil'
import { getI18nOrLabel } from '../../utils/StringUtil'
import { getColorFromPalette } from '../../utils/ColorUtil'

const DEFAULT_COLOR = 'black'

const getSerieLabel = (serieName, defaultLabel) => getI18nOrLabel(serieName) && serieName !== 'undefined' ? getI18nOrLabel(serieName) : defaultLabel

// Bande d'intervalle en echarts 6 : un socle transparent (0 -> borne basse) empilé avec un remplissage (borne basse -> borne haute)
const getBandSeries = ({ measures, name, color, stack, gridIndex }) => {
    const bounds = measures.map(m => {
        const isValid = hasValue(m.doubtMin) && hasValue(m.doubtMax)
        return {
            date: m.date,
            lo: isValid ? Math.min(m.doubtMin, m.doubtMax) : undefined,
            hi: isValid ? Math.max(m.doubtMin, m.doubtMax) : undefined,
        }
    })
    const baseSerie = {
        type: 'line',
        name,
        color,
        stack,
        symbol: 'none',
        lineStyle: { opacity: 0 },
        xAxisIndex: gridIndex,
        yAxisIndex: gridIndex,
    }
    return [
        {
            ...baseSerie,
            areaStyle: { opacity: 0 },
            data: bounds.map(({ date, lo }) => ({ value: [date, lo], isBandBase: true })),
        },
        {
            ...baseSerie,
            areaStyle: { color, opacity: 0.2 },
            data: bounds.map(({ date, lo, hi }) => ({
                value: [date, hasValue(lo) && hasValue(hi) ? hi - lo : null],
                range: [lo, hi],
            })),
        },
    ]
}

const getLineSerie = ({ measures, name, color, model, gridIndex }) => ({
    type: 'line',
    name,
    color,
    symbol: 'none',
    connectNulls: true,
    lineStyle: {
        color,
        width: model?.lineWidth || 2,
        type: model?.lineType || 'dashed',
        opacity: model?.lineOpacity || 1,
    },
    xAxisIndex: gridIndex,
    yAxisIndex: gridIndex,
    data: measures.map(m => ({ value: [m.date, m.value], isPred: true })),
})

const getPredSeries = ({ predMeasures = [], model, gridIndex }) => {
    if (!predMeasures.length) {
        return []
    }
    const source = predMeasures[0].source || model?.name
    const modelColor = model?.color || DEFAULT_COLOR
    const allSeries = groupBy(predMeasures, 'serieName')
    const nbLineSeries = keys(allSeries).filter(key => hasValue(allSeries[key][0].value)).length
    return keys(allSeries).flatMap((serieName, idx) => {
        const orderedMeasures = orderBy(allSeries[serieName], 'date')
        const band = hasValue(orderedMeasures[0].doubtMin) ? getBandSeries({
            measures: orderedMeasures,
            name: `${source} - ${getSerieLabel(serieName, 'Min - Max')}`,
            color: modelColor,
            stack: `${source}${idx}-${gridIndex}`,
            gridIndex,
        }) : []
        const line = hasValue(orderedMeasures[0].value) ? [getLineSerie({
            measures: orderedMeasures,
            name: `${source} - ${getSerieLabel(serieName, 'Prévision')}`,
            color: nbLineSeries > 1 ? getColorFromPalette(idx) : modelColor,
            model,
            gridIndex,
        })] : []
        return [...band, ...line]
    })
}

const getObservedSerie = ({ measures = [], name, gridIndex, simulationDate }) => ({
    type: 'line',
    name,
    color: '#0074e8',
    symbol: 'none',
    lineStyle: { type: 'solid', width: 1.5 },
    xAxisIndex: gridIndex,
    yAxisIndex: gridIndex,
    data: measures.map(([date, value]) => ({ value: [date, value] })),
    markLine: simulationDate ? {
        silent: true,
        symbol: 'none',
        label: { show: false },
        lineStyle: { type: 'dashed', color: '#555' },
        data: [{ xAxis: simulationDate }],
    } : undefined,
})

// Bornes de l'axe Y calculées sur la courbe observée et les lignes de prévision uniquement (sans les enveloppes)
const getMedianZoomBounds = ({ measures = [], predMeasures = [] }, minDate, maxDate) => {
    const isInWindow = date => (isNil(minDate) || date >= minDate) && (isNil(maxDate) || date <= maxDate)
    const valuess = [
        ...measures.filter(([date, value]) => isInWindow(date) && hasValue(value)).map(([, value]) => value),
        ...predMeasures.filter(m => isInWindow(m.date) && hasValue(m.value)).map(m => m.value),
    ]
    if (!valuess.length) {
        return {}
    }
    const minValue = Math.min(...valuess)
    const maxValue = Math.max(...valuess)
    const margin = (maxValue - minValue) * 0.05 || Math.abs(maxValue) * 0.05 || 1
    return { min: minValue - margin, max: maxValue + margin }
}

// Valeurs brutes du modèle : le calcul a retranché le gap (« hack ») aux valeurs et aux intervalles
const removeHack = result => ({
    ...result,
    predMeasures: result.predMeasures.map(m => hasValue(m.gap) ? {
        ...m,
        value: hasValue(m.value) ? m.value + m.gap : m.value,
        doubtMin: hasValue(m.doubtMin) ? m.doubtMin + m.gap : m.doubtMin,
        doubtMax: hasValue(m.doubtMax) ? m.doubtMax + m.gap : m.doubtMax,
    } : m),
})

const RAIN_COLOR = '#6200ff'
const OBSERVED_RAIN_COLOR = '#0d1b6e'

const getQuantile = (sortedValues, q) => {
    const position = (sortedValues.length - 1) * q
    const lower = Math.floor(position)
    const upper = Math.ceil(position)
    return sortedValues[lower] + (sortedValues[upper] - sortedValues[lower]) * (position - lower)
}

// Pluie prévue ECMWF Seeds : cumul journalier de chaque seed, puis quantiles des seeds pour chaque jour
const getRainPredStats = (rainPredMeasures = []) => {
    const byDay = groupBy(rainPredMeasures, ([date]) => moment(date).startOf('day').valueOf())
    return orderBy(keys(byDay).map(day => {
        const seedSums = values(groupBy(byDay[day], ([, , serieName]) => serieName))
            .map(seedMeasures => sumBy(seedMeasures, ([, value]) => value))
        const sorted = orderBy(seedSums)
        return {
            date: moment(parseInt(day)).hour(12).valueOf(),
            min: sorted[0],
            q1: getQuantile(sorted, 0.25),
            median: getQuantile(sorted, 0.5),
            q3: getQuantile(sorted, 0.75),
            max: sorted[sorted.length - 1],
        }
    }), 'date')
}

const DAY_MS = 24 * 3600 * 1000

// Boîte à moustache dessinée à la main : une série custom avec clip coupe proprement ce qui dépasse de la grid (le candlestick ne le fait pas)
const renderRainBoxPlot = (params, api) => {
    const date = api.value(0)
    const [xDay, yMin] = api.coord([date, api.value(1)])
    const [, yQ1] = api.coord([date, api.value(2)])
    const [, yMedian] = api.coord([date, api.value(3)])
    const [, yQ3] = api.coord([date, api.value(4)])
    const [, yMax] = api.coord([date, api.value(5)])
    const dayWidth = api.size([DAY_MS, 0])[0]
    const width = Math.min(dayWidth * 0.35, 8)
    // décalée à droite de la barre de pluie observée du même jour, pour ne pas la masquer
    const x = xDay + Math.min(dayWidth * 0.3, 8)
    return {
        type: 'group',
        children: [
            { type: 'line', shape: { x1: x, y1: yMin, x2: x, y2: yMax }, style: { stroke: 'black', lineWidth: 1 } },
            { type: 'rect', shape: { x: x - width / 2, y: Math.min(yQ1, yQ3), width, height: Math.abs(yQ3 - yQ1) }, style: { fill: RAIN_COLOR, stroke: 'black', lineWidth: 1 } },
            { type: 'circle', shape: { cx: x, cy: yMedian, r: 3.5 }, style: { fill: 'black' } },
        ],
    }
}

const getRainSeries = ({ rainMeasures = [], rainPredMeasures = [], gridIndex, rainAxisIndex }) => {
    const observed = rainMeasures.length ? [{
        type: 'bar',
        name: 'Pluie observée',
        color: OBSERVED_RAIN_COLOR,
        barMaxWidth: 8,
        xAxisIndex: gridIndex,
        yAxisIndex: rainAxisIndex,
        data: rainMeasures.map(([date, value]) => ({ value: [date, value] })),
    }] : []
    const stats = getRainPredStats(rainPredMeasures)
    const predicted = stats.length ? [{
        type: 'custom',
        name: 'Pluie prévue ECMWF',
        color: RAIN_COLOR,
        clip: true,
        xAxisIndex: gridIndex,
        yAxisIndex: rainAxisIndex,
        renderItem: renderRainBoxPlot,
        encode: { x: 0, y: [1, 5] },
        data: stats.map(s => ({ value: [s.date, s.min, s.q1, s.median, s.q3, s.max], rainStats: s })),
    }] : []
    return [...observed, ...predicted]
}

export {
    getPredSeries,
    getObservedSerie,
    getRainSeries,
    getMedianZoomBounds,
    removeHack,
}
